/**
 * Resume presentation styles — browser-preview token profiles.
 *
 * Single source of truth for the Classic / Modern / Compact preview appearance.
 * These tokens are surfaced to the paper preview as CSS custom properties so the
 * document responds to the selected style WITHOUT scattering `if (style === ...)`
 * through the JSX. Content, section order, and field meaning never change — only
 * font sizes, header/heading treatment, rule thickness, spacing and density.
 *
 * The PDF/DOCX exports have their own authoritative token profiles on the backend
 * (services/resume_style.py). The preview only needs to give a meaningful sense of
 * each style, not pixel-match Word.
 */

export const RESUME_STYLES = ['classic', 'modern', 'compact'];
export const DEFAULT_RESUME_STYLE = 'classic';

export function normalizeResumeStyle(value) {
  const v = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return RESUME_STYLES.includes(v) ? v : DEFAULT_RESUME_STYLE;
}

const SANS = "'Helvetica Neue', Helvetica, Arial, sans-serif";

const PROFILES = {
  classic: {
    fontFamily: SANS,
    padX: '52px', padY: '36px',
    headerAlign: 'center',
    namePx: '25px', nameTrack: '0.1em', nameWeight: 700,
    contactPx: '11px',
    headerRuleW: '2px', headerRuleC: 'rgb(40,50,66)',
    headingPx: '12.5px', headingTrack: '0.16em', headingColor: 'rgb(15,23,42)',
    sectionRuleW: '1.5px', sectionRuleC: 'rgba(51,65,85,0.85)',
    bodyPx: '11.5px', lineHeight: '1.55', sectionGap: '18px',
    compShade: 'transparent', compPad: '0px',
    sublabelPx: '11px',
  },
  modern: {
    fontFamily: SANS,
    padX: '64px', padY: '46px',
    headerAlign: 'left',
    namePx: '34px', nameTrack: '0.005em', nameWeight: 700,
    contactPx: '11px',
    headerRuleW: '1px', headerRuleC: 'rgb(207,216,204)',
    headingPx: '12px', headingTrack: '0.1em', headingColor: 'rgb(55,71,43)',
    sectionRuleW: '0.5px', sectionRuleC: 'rgba(148,163,184,0.55)',
    bodyPx: '11.5px', lineHeight: '1.72', sectionGap: '30px',
    compShade: 'rgb(240,244,235)', compPad: '12px 14px',
    sublabelPx: '11px',
  },
  compact: {
    fontFamily: SANS,
    padX: '40px', padY: '26px',
    headerAlign: 'left',
    namePx: '19px', nameTrack: '0.04em', nameWeight: 700,
    contactPx: '10px',
    headerRuleW: '1.25px', headerRuleC: 'rgb(40,50,66)',
    headingPx: '11px', headingTrack: '0.12em', headingColor: 'rgb(15,23,42)',
    sectionRuleW: '1.25px', sectionRuleC: 'rgba(51,65,85,0.85)',
    bodyPx: '10.5px', lineHeight: '1.32', sectionGap: '9px',
    compShade: 'transparent', compPad: '0px',
    sublabelPx: '10px',
  },
};

/** CSS custom-property map applied to the paper document root for the given style. */
export function resumeStyleVars(style) {
  const p = PROFILES[normalizeResumeStyle(style)];
  return {
    '--r-font': p.fontFamily,
    '--r-padx': p.padX,
    '--r-pady': p.padY,
    '--r-header-align': p.headerAlign,
    '--r-name': p.namePx,
    '--r-name-track': p.nameTrack,
    '--r-name-weight': p.nameWeight,
    '--r-contact': p.contactPx,
    '--r-hrule-w': p.headerRuleW,
    '--r-hrule-c': p.headerRuleC,
    '--r-heading': p.headingPx,
    '--r-heading-track': p.headingTrack,
    '--r-heading-color': p.headingColor,
    '--r-srule-w': p.sectionRuleW,
    '--r-srule-c': p.sectionRuleC,
    '--r-body': p.bodyPx,
    '--r-lh': p.lineHeight,
    '--r-section-gap': p.sectionGap,
    '--r-comp-shade': p.compShade,
    '--r-comp-pad': p.compPad,
    '--r-sublabel': p.sublabelPx,
  };
}
